/**
 * Learning sidebar component.
 *
 * Navigation sidebar for learning section. Provides content type switching
 * (grammar, sentences) and proficiency level filtering with visual
 * indicators for active selections.
 *
 * @module LearningSidebar
 */

import { useMemo } from "react";
import { Lock, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LEARNING_CONTENT_DESTINATIONS } from "@/data/learningNavigation";
import { ContentType, ProficiencyLevel } from "@/types/learning";

const CONTENT_TYPE_LABEL_FALLBACK: Record<ContentType, string> = {
  grammar: "Grammar",
  sentences: "Common Sentences",
  books: "PDF Books",
};
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface LearningSidebarProps {
  readonly activeContentType: ContentType;
  readonly selectedLevels: ProficiencyLevel[];
  readonly onLevelToggle: (level: ProficiencyLevel) => void;
  readonly onClearLevels: () => void;
  readonly isOpen?: boolean;
  readonly onClose?: () => void;
}

export function LearningSidebar({
  activeContentType,
  selectedLevels,
  onLevelToggle,
  onClearLevels,
  isOpen = false,
  onClose,
}: LearningSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const contentTypes = LEARNING_CONTENT_DESTINATIONS.map((dest) => ({
    id: dest.id,
    label: t(`learning.sidebar.types.${dest.id}`, {
      defaultValue: CONTENT_TYPE_LABEL_FALLBACK[dest.id],
    }),
    icon: dest.icon,
  }));

  const levels: ProficiencyLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const lockedLevels = useMemo(
    () => new Set<ProficiencyLevel>(["A2", "B1", "B2", "C1", "C2"]),
    [],
  );

  const isLevelLocked = (level: ProficiencyLevel) => {
    return lockedLevels.has(level);
  };

  const handleContentTypeChange = (type: ContentType) => {
    const dest = LEARNING_CONTENT_DESTINATIONS.find((d) => d.id === type);
    if (dest) {
      navigate(dest.path);
    }
  };

  const handleLevelToggle = (level: ProficiencyLevel) => {
    onLevelToggle(level);
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Mobile Close Button */}
      {onClose && (
        <div className="flex justify-end lg:hidden mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Content Type Switcher */}
      <div className="mb-8">
        <h3 className="font-ui font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
          {t("learning.sidebar.contentType")}
        </h3>
        <nav className="space-y-1">
          {contentTypes.map((type) => {
            const Icon = type.icon;
            const isActive = activeContentType === type.id;

            const getButtonClassName = () => {
              if (isActive) {
                return "bg-primary text-primary-foreground";
              }
              return "text-foreground hover:bg-accent";
            };

            return (
              <button
                key={type.id}
                type="button"
                onClick={() => {
                  handleContentTypeChange(type.id);
                  onClose?.();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
                  getButtonClassName(),
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="font-ui text-sm font-medium">
                  {type.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Level Filter */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-ui font-semibold text-sm text-muted-foreground uppercase tracking-wider">
            {t("learning.sidebar.level", { defaultValue: "Level" })}
          </h3>
          {selectedLevels.length > 0 && (
            <button
              onClick={onClearLevels}
              className="font-ui text-xs text-primary hover:underline"
            >
              {t("learning.sidebar.clear", { defaultValue: "Clear" })}
            </button>
          )}
        </div>
        <div className="space-y-2">
          {levels.map((level) => {
            const isLocked = isLevelLocked(level);
            return (
              <label
                key={level}
                className={cn(
                  "flex items-center gap-2 p-2 rounded transition-colors group",
                  isLocked
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-accent/50",
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedLevels.includes(level)}
                  onChange={() => !isLocked && handleLevelToggle(level)}
                  disabled={isLocked}
                  className={cn(
                    "rounded border-border text-primary focus:ring-primary h-4 w-4",
                    isLocked && "grayscale pointer-events-none",
                  )}
                />
                <div className="flex items-center justify-between flex-1">
                  <span
                    className={cn(
                      "font-ui text-sm transition-colors",
                      isLocked
                        ? "text-muted-foreground"
                        : "text-foreground group-hover:text-primary",
                    )}
                  >
                    {level}
                  </span>
                  {isLocked && (
                    <Lock className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-[280px] shrink-0 bg-card border-r border-border p-6 h-full overflow-y-auto">
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div
            className="fixed inset-0 bg-background/80 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />
          <aside className="safe-area-top safe-area-bottom relative h-[100dvh] w-[min(85vw,280px)] overflow-y-auto border-r border-border bg-card p-4 shadow-xl animate-in slide-in-from-left duration-300 sm:p-6">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
